import './maxDiscountModal.scss';

import { useState } from 'react';

import { Modal, TextInput } from '@/components/atoms';

interface MaxDiscountModalProps {
  showModal: boolean;
  onCloseModal: () => void;
  maxDiscountValue: number;
  onSaveDiscount: (newDiscount: number) => Promise<void>;
}

export default function MaxDiscountModal({
  showModal,
  onCloseModal,
  maxDiscountValue,
  onSaveDiscount,
}: MaxDiscountModalProps) {
  const [maxDiscount, setMaxDiscount] = useState('');
  const [error, setError] = useState('');

  const handleInputChange = (value: string) => {
    const isValid = /^\d+(\.\d{0,4})?$/.test(value) || value === '';

    if (isValid) {
      setMaxDiscount(value);
      setError('');
    } else {
      setError('Max discount cannot have more than four decimal places');
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    const { key, currentTarget } = e;
    const navigationKeys = ['Backspace', 'Tab', 'ArrowLeft', 'ArrowRight', 'Delete'];
    const decimalKeys = ['.', 'Decimal'];

    if (key >= '0' && key <= '9') {
      return;
    }

    if (navigationKeys.includes(key)) {
      return;
    }

    if (decimalKeys.includes(key)) {
      if (!currentTarget.value.includes('.')) {
        return;
      }
    }

    e.preventDefault();
  };

  const handleSecondaryButtonClick = () => {
    setError(''); // Clear error on close
    onCloseModal();
  };

  const handlePrimaryButtonClick = async () => {
    const numericMaxDiscount = parseFloat(maxDiscount);
    if (!isNaN(numericMaxDiscount)) {
      try {
        await onSaveDiscount(numericMaxDiscount);
         setMaxDiscount('')
        setError(''); // Clear error on success
        onCloseModal();
      } catch (error: any) {
        // Only show backend validation error for 400 with details.maxDiscountPercentage
        let backendError = '';
        if (error && typeof error === 'object') {
          const details = error.details || error?.data?.details;
          if (details && typeof details === 'object' && details.maxDiscountPercentage) {
            backendError = details.maxDiscountPercentage;
          }
        }
        setError(backendError);
      }
    } else {
      console.error('Invalid max discount input');
      setError('Invalid max discount input');
    }
  };

  return (
      <Modal
        id="max-discount-modal"
        title="Max Discount"
        text="Change the max discount percentage?"
        showModal={showModal}
        onCloseModal={onCloseModal}
        primaryButtonText="Save"
        showSecondaryButton={true}
        secondaryButtonText="Cancel"
        isDark={false}
        className="product-admin-max-discount-modal"
        onClickSecondaryButton={() => { handleSecondaryButtonClick(); setMaxDiscount(''); }}
        onClickPrimaryButton={()=>{handlePrimaryButtonClick()}}
        primaryButtonDisabled={!maxDiscount || Boolean(error)}
        content={
          <div className="flex flex-col justify-center gap-4 w-[500px]">
            <div className="flex flex-row items-center gap-40">
              <p className="text-sm font-medium text-[#D0D0D0] whitespace-nowrap">Current Max</p>
              <p className="text-sm font-bold text-[#D0D0D0]">{maxDiscountValue}%</p>
            </div>

            <hr className="border-t border-gray-300" />

            <div className="flex flex-row items-center gap-40">
              <p className="text-sm font-medium text-[#D0D0D0] whitespace-nowrap">New Max</p>
              <TextInput
                placeholderText="0.0"
                value={maxDiscount}
                onChange={value => { handleInputChange(value); setError(''); }}
                error={error}
                handleKeyDown={handleKeyDown}
              />
            </div>
          </div>
        }
      />
  );
}
